import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlarmClock, Bell, Check, CheckCheck } from 'lucide-react';
import { cardPath, type Notification } from '@kaba/shared';
import { api } from '../lib/api';
import { queryClient } from '../lib/board';
import { NOTIFICATION_TEXT } from '../lib/realtime';
import { navigate } from '../lib/router';
import { Button, cx, DropdownContent, DropdownItem, DropdownLabel, DropdownMenu, DropdownTrigger, IconButton, Sheet } from './ui/ui';
import { formatWhen, snooze, snoozeChoices } from '../lib/reminders';
import { toast } from '../lib/toast';

type Data = { notifications: Notification[]; unread: number };

export function useNotifications() {
  return useQuery({ queryKey: ['notifications'], queryFn: () => api.get<Data>('/api/notifications'), staleTime: 60_000 });
}

function markRead(ids?: number[]) {
  queryClient.setQueryData<Data>(['notifications'], (d) =>
    d
      ? {
          unread: ids ? Math.max(0, d.unread - d.notifications.filter((n) => ids.includes(n.id) && !n.readAt).length) : 0,
          notifications: d.notifications.map((n) => (!ids || ids.includes(n.id) ? { ...n, readAt: n.readAt ?? new Date().toISOString() } : n)),
        }
      : d,
  );
  void api.post('/api/notifications/read', ids ? { ids } : {});
}

function timeAgo(iso: string) {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'gerade eben';
  if (s < 3600) return `vor ${Math.floor(s / 60)} Min.`;
  if (s < 86400) return `vor ${Math.floor(s / 3600)} Std.`;
  return new Date(iso).toLocaleDateString('de-DE');
}

export function InboxButton() {
  const { data } = useNotifications();
  const [open, setOpen] = useState(false);
  const unread = data?.unread ?? 0;
  return (
    <>
      <IconButton label={unread ? `Posteingang, ${unread} ungelesen` : 'Posteingang'} onClick={() => setOpen(true)} className="relative">
        <Bell size={19} />
        {unread > 0 && (
          <span className="absolute top-1.5 right-1.5 inline-flex min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </IconButton>
      <Sheet open={open} onOpenChange={setOpen} title="Posteingang">
        <div className="flex flex-col">
          {unread > 0 && (
            <div className="flex justify-end border-b border-[var(--line)] p-2">
              <Button size="sm" variant="ghost" onClick={() => markRead()}>
                <CheckCheck size={16} /> Alle als gelesen markieren
              </Button>
            </div>
          )}
          {data?.notifications.length === 0 && (
            <p className="p-6 text-center text-sm text-[var(--ink-soft)]">
              Hier erscheint, wenn dich jemand erwähnt, dir eine Karte zuweist oder eine beobachtete Karte kommentiert.
            </p>
          )}
          <ul className="divide-y divide-[var(--line)]">
            {data?.notifications.map((n) => (
              <li key={n.id} className={cx('flex items-start', !n.readAt && 'bg-accent-soft/60')}>
                <button
                  className="flex min-w-0 flex-1 gap-3 px-4 py-3 text-left hover:bg-black/[0.03] dark:hover:bg-white/5"
                  onClick={() => {
                    markRead([n.id]);
                    setOpen(false);
                    if (n.boardId) navigate(cardPath(n.boardId, n.cardId, n.commentId));
                  }}
                >
                  <span className={cx('mt-1.5 size-2 shrink-0 rounded-full', n.readAt ? 'bg-transparent' : 'bg-accent')} aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm">
                      {!['due', 'automation', 'reminder', 'handover', 'form'].includes(n.type) && <b>{n.actorName ?? 'Jemand'} </b>}
                      {NOTIFICATION_TEXT[n.type]}
                      {n.cardTitle && <> – „{n.cardTitle}“</>}
                    </span>
                    {n.text && <span className="mt-0.5 line-clamp-2 block text-sm text-[var(--ink-soft)]">{n.text}</span>}
                    <span className="mt-0.5 block text-xs text-[var(--ink-soft)]">
                      {n.boardTitle} · {timeAgo(n.createdAt)}
                      {!n.readAt && <span className="sr-only"> (ungelesen)</span>}
                    </span>
                  </span>
                </button>
                <div className="flex shrink-0 flex-col py-1 pr-1">
                  {!n.readAt && (
                    <IconButton label="Als gelesen markieren" onClick={() => markRead([n.id])}>
                      <Check size={16} />
                    </IconButton>
                  )}
                  {n.cardId && (
                    <DropdownMenu>
                      <DropdownTrigger asChild>
                        <IconButton label={`Später an „${n.cardTitle ?? 'diese Karte'}“ erinnern`}>
                          <AlarmClock size={16} />
                        </IconButton>
                      </DropdownTrigger>
                      <DropdownContent align="end">
                        <DropdownLabel>Für mich später erinnern</DropdownLabel>
                        {snoozeChoices().map((c) => (
                          <DropdownItem
                            key={c.label}
                            onSelect={async () => {
                              try {
                                await snooze(n.cardId!, c.at);
                                markRead([n.id]);
                                toast.info(`Du wirst ${formatWhen(c.at.toISOString())} erinnert.`);
                              } catch (err) {
                                toast.error((err as Error).message);
                              }
                            }}
                          >
                            {c.label}
                          </DropdownItem>
                        ))}
                      </DropdownContent>
                    </DropdownMenu>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </Sheet>
    </>
  );
}
